'use client';

import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { COOKIE_SETTINGS_EVENT } from '@/lib/cookie-settings';

/**
 * Defers the entire c15t consent stack (~300KB) until the browser is idle so
 * it never competes with first paint or hydration. Children render
 * immediately; the banner/dialog/analytics scripts mount 1-2s later, or
 * instantly when the /legal "Cookie settings" button asks for them.
 *
 * Some routes are standalone surfaces rather than pages of the portfolio, and
 * the consent stack (with every consent-gated analytics script) is skipped on
 * them entirely: /gringa is a conversion page where a banner would cover the
 * CTA, /invoice is a document a client opens to pay a bill, and /tools is a
 * private working surface. None of them sets an analytics cookie, so there is
 * nothing to consent to.
 */

const CONSENT_FREE_ROUTES = ['/gringa', '/invoice', '/tools'];

const ConsentSystem = dynamic(
  () => import('./consent-system').then((mod) => mod.ConsentSystem),
  { ssr: false }
);

export function ConsentManager({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const consentFree = CONSENT_FREE_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  );
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (consentFree) return;

    let idleId: number | undefined;
    let timeoutId: number | undefined;
    const load = () => setReady(true);

    // Instant path: the cookie-settings button was clicked before idle.
    window.addEventListener(COOKIE_SETTINGS_EVENT, load);

    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(load, { timeout: 2500 });
    } else {
      timeoutId = window.setTimeout(load, 1500);
    }

    return () => {
      window.removeEventListener(COOKIE_SETTINGS_EVENT, load);
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [consentFree]);

  return (
    <>
      {children}
      {ready && !consentFree && <ConsentSystem />}
    </>
  );
}
